import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Trash2, Plus, Loader2, Eye } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import { usePermission } from '../context/PermissionContext';
import CategorySelector from '../components/CategorySelector';
import UniversalList from '../components/UniversalList';

const PAGE_SIZE_DEFAULT = 20;

const CategoryAttributeRulePage = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { hasPermission } = usePermission();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attributeSets, setAttributeSets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('add');
  const [viewMode, setViewMode] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formCategoryId, setFormCategoryId] = useState('');
  const [formSetId, setFormSetId] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);

  const canAdd = hasPermission('settings_category_attribute_rules', 'add');
  const canDelete = hasPermission('settings_category_attribute_rules', 'delete');
  const canView = hasPermission('settings_category_attribute_rules', 'view');

  const fetchRules = useCallback(async () => {
    try {
      const res = await fetch('/api/settings/category-attribute-rules');
      const data = await res.json();
      if (data.status === 'success') setRules(data.data || []);
    } catch (e) {
      console.error('Failed to load rules', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAttributeSets = useCallback(async () => {
    try {
      const res = await fetch('/api/settings/attribute-sets?entity_type=product');
      const data = await res.json();
      if (data.status === 'success') setAttributeSets(data.data || []);
    } catch (e) {
      console.error('Failed to load attribute sets', e);
    }
  }, []);

  useEffect(() => {
    fetchRules();
    fetchAttributeSets();
  }, [fetchRules, fetchAttributeSets]);

  // Reset page on search
  useEffect(() => { setPage(1); }, [searchTerm]);

  // Client-side filter
  const filteredRules = useMemo(() => {
    if (!searchTerm) return rules;
    const q = searchTerm.toLowerCase();
    return rules.filter(r =>
      (r.category_name || '').toLowerCase().includes(q) ||
      (r.set_name || '').toLowerCase().includes(q) ||
      (r.set_code || '').toLowerCase().includes(q)
    );
  }, [rules, searchTerm]);

  // Client-side paginate
  const totalItems = filteredRules.length;
  const pageData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRules.slice(start, start + pageSize);
  }, [filteredRules, page, pageSize]);

  const openAddForm = () => {
    setFormMode('add');
    setViewMode(false);
    setEditingRule(null);
    setFormCategoryId('');
    setFormSetId('');
    setShowForm(true);
  };

  const openView = (rule) => {
    setFormMode('view');
    setViewMode(true);
    setEditingRule(rule);
    setFormCategoryId(String(rule.category_id));
    setFormSetId(String(rule.attribute_set_id));
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setViewMode(false);
    setEditingRule(null);
    setFormCategoryId('');
    setFormSetId('');
  };

  const handleSave = async () => {
    const catId = parseInt(formCategoryId, 10);
    const setId = parseInt(formSetId, 10);
    if (!catId || !setId) {
      notify(t('common.fill_required_fields'), 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch('/api/settings/category-attribute-rules/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category_id: catId, attribute_set_id: setId }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        notify(t('common.save_success'), 'success');
        closeForm();
        fetchRules();
      } else {
        notify(data.message || t('common.save_failed'), 'error');
      }
    } catch (e) {
      notify(t('common.network_error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm(t('common.confirm_delete'))) return;
    try {
      const res = await fetch(`/api/settings/category-attribute-rules/delete/${ruleId}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (data.status === 'success') {
        notify(t('common.delete_success'), 'success');
        fetchRules();
      } else {
        notify(data.message || t('common.delete_failed'), 'error');
      }
    } catch (e) {
      notify(t('common.network_error'), 'error');
    }
  };

  const columns = [
    {
      key: 'category_name',
      label: t('common.category'),
      className: 'text-slate-700 dark:text-slate-300',
      render: (val, row) => (
        <span>{val}<span className="text-slate-400 ml-2">(ID: {row.category_id})</span></span>
      ),
    },
    {
      key: 'set_name',
      label: t('settings.attribute_sets'),
      className: 'text-slate-700 dark:text-slate-300',
      render: (val, row) => (
        <span>{val}<span className="text-slate-400 ml-2">({row.set_code})</span></span>
      ),
    },
    {
      key: '_actions',
      label: t('common.actions'),
      headerClassName: 'text-right',
      className: 'text-right',
      render: (_, row) => (
        <div className="flex items-center justify-end gap-1">
          {canView && (
            <button onClick={() => openView(row)}
              className="p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              title={t('common.view')}>
              <Eye size={16} />
            </button>
          )}

          {canDelete && (
            <button onClick={() => handleDelete(row.id)}
              className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              title={t('common.delete')}>
              <Trash2 size={16} />
            </button>
          )}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-6">
        {/* Title */}
        <div>
          <h1 className="text-2xl font-bold">{t('settings.category_attribute_rules')}</h1>
          <p className="text-slate-500 text-sm dark:text-slate-400 mt-1">{t('settings.category_attribute_rules_desc')}</p>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
          <div className="md:hidden" />
          <div className="flex flex-wrap gap-3 md:justify-end">
            {canAdd && (
              <button onClick={openAddForm}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                <Plus size={18} />
                {t('common.add')}
              </button>
            )}
          </div>
        </div>

        <UniversalList
          searchPlaceholder={t('common.search')}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          columns={columns}
          data={pageData}
          loading={false}
          emptyMessage={t('common.no_records')}
          rowKey="id"
          page={page}
          totalItems={totalItems}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
          onRowClick={(row) => openView(row)}
        />
      </div>

      {/* Add/Edit/View inline form */}
      {showForm && (
        <div className="mt-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm space-y-4">
          <h3 className="font-medium text-slate-700 dark:text-slate-300 text-base border-b border-slate-200 dark:border-slate-700 pb-3">
            {viewMode
              ? t('common.view')
              : t('common.add')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                {t('common.category')} <span className="text-red-500">*</span>
              </label>
              <CategorySelector
                value={formCategoryId ? parseInt(formCategoryId, 10) : ''}
                onChange={(val) => setFormCategoryId(val ? String(val) : '')}
                viewOnly={viewMode}
                selectedName={viewMode ? editingRule?.category_name : ''}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                {t('settings.attribute_sets')} <span className="text-red-500">*</span>
              </label>
              {viewMode ? (
                <div className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-transparent text-sm text-slate-700 dark:text-slate-300">
                  {editingRule?.set_name} ({editingRule?.set_code})
                </div>
              ) : (
                <select
                  value={formSetId}
                  onChange={(e) => setFormSetId(e.target.value)}
                  className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-transparent dark:text-white"
                >
                  <option value="">-- {t('common.select')} --</option>
                  {attributeSets.map((s) => (
                    <option key={s.set_id} value={s.set_id}>
                      {s.set_name} ({s.set_code})
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <button onClick={closeForm}
              className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors text-sm font-medium">
              {t('common.cancel')}
            </button>
            {!viewMode && (
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60 text-sm font-medium">
                {saving && <Loader2 size={16} className="animate-spin" />}
                {t('common.save')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryAttributeRulePage;
